import React, { useMemo, useRef, useState } from "react";
import { Input } from "@progress/kendo-react-inputs";
import { Popup } from "@progress/kendo-react-popup";
import { Tooltip } from "@progress/kendo-react-tooltip";
import { button, icon, label, notifyIcon, number, quote, tooltip } from "../../../../../config";
import { useTeamChipPersistence } from "../../../../../shared/components/ChipEditor/TeamChipPersistenceContext";
import { getNotification } from "../../../../../utils/common";
import { onBlur, onFocus, onOpen } from "../../../../../utils/kendo";
import { useWorkflowContext } from "../../../WorkflowContextProvider";
import { getLinkedAdminPresetLabel } from "../../adminVariableLink.helper";
import { useVariablesContext } from "../../context/variableContext";
import { useLockTeamChipHeaderEdits, useUniqueVariableNames } from "../../hooks/workflowVariables.hooks";
import { VARIABLE_ICONS, WORKFLOW_VARIABLE_LABELS } from "../../workflowVariables.constants";

/**
 * Common variable name header component used across all variable configurations
 * @param {Object} props - Component props
 * @param {Function} props.removeVariableHandler - Function to remove variable
 * @author Abhishek Dhillon
 * @parent DropdownConfiguration, InputConfiguration, DateConfiguration, NumberConfiguration
 */
const VariableNameHeader = ({
  isNameEmpty,
  setIsNameEmpty,
  removeVariableHandler,
  selectedVariable,
  handleVariableChange,
  formData,
  formId,
  isFormVariable,
  isDeleteDisabled = false,
  onVariableNameCommitted,
  onPanelClose,
}) => {
  const [isEditName, setIsEditName] = useState(false);
  const [isEditDescription, setIsEditDescription] = useState(false);
  const [showDeletePopup, setShowDeletePopup] = useState(false);
  const { isNewWorkflowVariable, workflowDefaults } = useWorkflowContext();
  const { variableName, description, setDescription, isDuplicateName, setHasVarChanges, setVariableName } = useVariablesContext();
  const { checkIsDuplicateName } = useUniqueVariableNames();
  const lockHeaderEdits = useLockTeamChipHeaderEdits(isFormVariable, selectedVariable);
  const teamChipPersist = useTeamChipPersistence();
  const teamChipNameEmphasis = useMemo(
    () => Boolean(!isFormVariable && teamChipPersist?.projectId && teamChipPersist?.lockPresetFields && !lockHeaderEdits),
    [isFormVariable, teamChipPersist?.projectId, teamChipPersist?.lockPresetFields, lockHeaderEdits]
  );
  const nameRowClass = teamChipNameEmphasis ? " team-chip-global-name-emphasis" : "";
  const deletePopupRef = useRef(null);
  const blurTimeoutRef = useRef(null);
  const linkedVariableName =
    selectedVariable?.adminType === WORKFLOW_VARIABLE_LABELS.IS_ADMIN_FIELD
      ? getLinkedAdminPresetLabel(selectedVariable, workflowDefaults?.adminCustomVariables)
      : selectedVariable?.variableId?.variableName;

  const handleDescriptionChange = (e) => {
    setDescription(e.target.value);
    // handleVariableChange();
  };

  const handleVariableNameChange = (event) => {
    const newVariableName = event.target.value;
    setVariableName(newVariableName);
    checkIsDuplicateName(newVariableName?.trim());
    if (!newVariableName?.trim()) {
      setIsNameEmpty(true);
      setHasVarChanges(false);
      return;
    }
    setIsNameEmpty(false);
    // handleVariableChange();
    return;
  };

  const handleVariableNameCheck = () => {
    if (!isNameEmpty && !isDuplicateName) {
      setIsEditName(false);
      handleVariableChange();
      if (typeof onVariableNameCommitted === "function") {
        void Promise.resolve(onVariableNameCommitted()).catch(() => {});
      }
    }
    return;
  };

  /**
   * Handler to show delete confirmation popup
   * @author Abhishek Dhillon
   */
  const handleDeleteClick = () => {
    if (isDeleteDisabled) {
      getNotification(quote.TEAM_CHIPS_MANAGE_DISABLED, notifyIcon.WARNING_ICON);
      return;
    }
    setShowDeletePopup(true);
  };

  /**
   * Handler to hide delete confirmation popup
   * @author Abhishek Dhillon
   */
  const hideDeletePopup = () => {
    setShowDeletePopup(false);
  };

  /**
   * Handler for confirming variable deletion
   * @author Abhishek Dhillon
   */
  const handleConfirmDelete = () => {
    removeVariableHandler();
    setShowDeletePopup(false);
  };

  /**
   * Calculates the usage count of the variable in the forms
   * @author Abhishek Dhillon
   */
  const usageCount = useMemo(() => {
    if (!Array.isArray(selectedVariable?.usedInForms)) {
      return number.ZERO;
    }
    return selectedVariable.usedInForms.filter((id) => formData?.id !== id).length;
  }, [selectedVariable?.usedInForms, formData?.id]);

  return (
    <div className='d-flex flex-column'>
      {/* Variable Name Section */}
      <div className='d-flex justify-content-between align-items-start w-100 gap-2'>
        {!isEditName ? (
          <div className={`form-sub-heading mb-3 d-flex align-items-center gap-2 flex-grow-1 min-w-0 variable-name-hover-container${nameRowClass}`}>
            <Tooltip position='bottom' anchorElement='target' parentTitle={true}>
              <span className='variable-icon mt-1 flex-shrink-0' title={selectedVariable?.type}>
                {selectedVariable?.type === WORKFLOW_VARIABLE_LABELS.DROPDOWN
                  ? selectedVariable?.attributes?.numberOnly
                    ? VARIABLE_ICONS.number_dropdown
                    : VARIABLE_ICONS.dropdown
                  : VARIABLE_ICONS[selectedVariable?.type]
                  ? VARIABLE_ICONS[selectedVariable?.type]
                  : VARIABLE_ICONS.dropdown}
              </span>
            </Tooltip>
            <div className={`text-truncate min-w-0 flex-grow-1 font-weight-normal`}>{variableName}</div>
            {!lockHeaderEdits && (
              <span className='cursor-pointer variable-edit-icon app-icon-medium' onClick={() => setIsEditName(true)} id='edit-variable-name-btn'>
                {icon.EDIT_ICON}
              </span>
            )}
          </div>
        ) : (
          <div className={`form-group mb-2 d-flex align-items-center gap-2 flex-grow-1 min-w-0${nameRowClass}`}>
            <label className="mb-0">{label.NAME}</label>
            <Input
              autoFocus={true}
              placeholder='Enter Name'
              value={variableName}
              maxLength={number.TWENTY_FIVE}
              onChange={handleVariableNameChange}
              id='variable-name-input'
              className='form-control form-control-sm'
            />
            {(isDuplicateName || isNameEmpty) && (
              <small className='text-danger'>
                {isDuplicateName && !isNameEmpty ? WORKFLOW_VARIABLE_LABELS.VARIABLE_NAME_DUPLICATE_ERROR : ""}
                {isNameEmpty ? WORKFLOW_VARIABLE_LABELS.VARIABLE_NAME_REQUIRED_ERROR : ""}
              </small>
            )}
            {!isNameEmpty && !isDuplicateName && (
              <span className='cursor-pointer' onClick={handleVariableNameCheck} id='confirm-variable-name-btn'>
                {icon.CHECKBOX}
              </span>
            )}
          </div>
        )}
        <div className='d-flex justify-content-end align-items-center flex-shrink-0 pt-1 gap-2'>
          {!lockHeaderEdits ? (
            <span
              onClick={handleDeleteClick}
              id='delete-variable-btn'
              className={`cursor-pointer app-icon-medium${isDeleteDisabled ? " team-chip-delete-restricted" : ""}`}
              title={tooltip.DELETE_CHIP}>
              <Tooltip position='left' anchorElement='target' parentTitle={true}>
                {icon.TRASH}
              </Tooltip>
            </span>
          ) : (
            <span className='app-icon-medium'>{icon.LOCK_ICON}</span>
          )}
          {typeof onPanelClose === "function" && (
            <span
              role='button'
              tabIndex={0}
              className='cursor-pointer app-icon-medium'
              title={label.CLOSE}
              aria-label={label.CLOSE}
              onClick={() => onPanelClose()}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  onPanelClose();
                }
              }}>
              <Tooltip position='left' anchorElement='target' parentTitle={true}>
                {icon.CLOSE}
              </Tooltip>
            </span>
          )}
          {showDeletePopup && (
            <Popup
              show={showDeletePopup}
              anchor={document.getElementById("delete-variable-btn")}
              animate={true}
              onOpen={() => onOpen(deletePopupRef)}
              collision={{ horizontal: "fit", vertical: "fit" }}
              popupClass='dt-popup'>
              <div
                ref={deletePopupRef}
                tabIndex={number.ZERO}
                onFocus={() => onFocus(blurTimeoutRef)}
                onBlur={() => onBlur(blurTimeoutRef, hideDeletePopup)}>
                <div className='p-3'>
                  {usageCount === number.ZERO ? (
                    <>
                      <label htmlFor='delete-confirmation'>{label.CONFIRM}</label>
                      <div className='mb-3'>
                        <small className='text-nowrap'>Are you sure you want to delete this chip ?</small>
                      </div>
                      <div className='d-flex align-items-center justify-content-end gap-2'>
                        <button type='button' id='cancel-delete-variable' className='btn btn-sm btn-ghost' onClick={hideDeletePopup}>
                          {button.NO}
                        </button>
                        <button type='button' id='confirm-delete-variable' className='btn btn-sm btn-danger' onClick={handleConfirmDelete}>
                          {button.YES}
                        </button>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className='mb-3 max-width-200'>
                        <small>{WORKFLOW_VARIABLE_LABELS.DELETE_CHIP_ALREADY_IN_USE}</small>
                      </div>
                      <div className='d-flex align-items-center gap-2'>
                        <button type='button' id='close-delete-variable' className='btn btn-sm btn-primary' onClick={hideDeletePopup}>
                          {button.CLOSE}
                        </button>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </Popup>
          )}
        </div>
      </div>

      {/* Description Section - Always visible regardless of name edit mode */}
      {linkedVariableName ? (
        <div className='form-group'>
          <div className='d-flex align-items-center mb-2'>
            <span className='mr-2 grey-linkage pointer-events-none'>{icon.LOCK_ICON}</span>
            <span className='grey-linkage pointer-events-none'>Linked to {linkedVariableName} (Global Chip)</span>
          </div>
          <div className='variable-description-box'>
            {(selectedVariable?.variableId?.description || selectedVariable?.description || "").trim() ||
              WORKFLOW_VARIABLE_LABELS.NO_CHIP_DESCRIPTION_LABEL}
          </div>
        </div>
      ) : selectedVariable?.adminType !== WORKFLOW_VARIABLE_LABELS.IS_ADMIN_FIELD ? (
        !isEditDescription ? (
          <div className='form-group variable-description-hover-container'>
            <div className='variable-description-box d-flex align-items-start gap-2'>
              <span className='flex-grow-1'>
                {(description || "").trim() ? (description || "").trim() : WORKFLOW_VARIABLE_LABELS.NO_CHIP_DESCRIPTION_LABEL}
              </span>
              {!lockHeaderEdits ? (
                <span
                  className='cursor-pointer variable-description-edit-icon flex-shrink-0 app-icon-small'
                  onClick={() => setIsEditDescription(true)}
                  id='edit-variable-description-btn'>
                  {icon.EDIT_ICON}
                </span>
              ) : null}
            </div>
          </div>
        ) : (
          <div className='form-group mb-3 d-flex align-items-center gap-2'>
            <Input
              autoFocus
              placeholder='Enter Description (max 100 characters)'
              value={description}
              maxLength={100}
              onChange={handleDescriptionChange}
              onBlur={handleVariableChange}
              id='variable-description-input'
              className='form-control form-control-sm'
            />
            <span
              className='cursor-pointer'
              onClick={() => {
                setIsEditDescription(false);
                handleVariableChange();
              }}
              id='confirm-variable-description-btn'>
              {icon.CHECKBOX}
            </span>
          </div>
        )
      ) : (
        <></>
      )}
    </div>
  );
};

export default VariableNameHeader;
